import React, {useMemo} from "react";
import moment from "moment";
import { DateFormats } from "../../../components/constants/securspace-constants";
import { PersonAndBoxMessage } from "../../../components/messages/PersonAndBoxMessage";
import { useSnackbar } from "../../../context/snackbar-context";
import { useSnackbarDisplayOnError } from "../../../hooks/error/useSnackbarDisplayOnError";
import { useBindSearchQuery } from "../../../hooks/useBindSearchQuery";
import { useGetBoundReportPaginated } from "../../../hooks/request/useGetBoundReportPaginated";
import { useGetBoundReportSummary } from "../../../hooks/request/useGetBoundReportSummary";
import { useBoundQueryDownloadUrl } from "../../../hooks/report/useBoundQueryDownloadUrl";
import DownloadCSVButton from "../../../components/report/export/DownloadCSVButton";
import { BoundTablePagination } from "../../../components/common/inputs/boundInputs";
import { BookedSpaceCalendarView } from "../../../types/model/orm/BookedSpaceCalendarView";
import { AdminSpaceUsageSortFilter, AdminSpaceUsageSearchParams } from "../../../components/admin/spaceUsage/AdminSpaceUsageSortFilter";
import { ADMIN_SPACE_USAGE_DATE_BYPASS_KEYS } from "../../../types/model/criteria/spaceUtilizationCriteria/AdminSpaceUtilizationCriteria";
import { withDateRangeBypass } from "../../../util/ReportDateRangeUtils";
import { AdminSpaceUsageCardListWithLoader } from "../../../components/admin/spaceUsage/AdminSpaceUsageCardList";
import { adminSpaceUsageReportEndpoints as reportEndpoints } from "../../../requests/api-endpoints";
import { SpaceUsageSummary, adminSpaceUsageSummaryMapping } from "../../../types/model/valueObject/spaceutil/SpaceUsageSummary";
import {
  DownloadButtonsContainer,
  ReportDataSummary,
  ReportSummaryWithLoader
} from "../../../components/report/redesigned";
import {
  SecurSpaceContainer,
  SecurSpaceReportContainer,
  SecurSpaceReportTitle
} from "../../../components/common";

// a default query MUST be defined outside of any components to avoid re-initialization on each render
const DEFAULT_QUERY: AdminSpaceUsageSearchParams = {
  startDate: moment().subtract(1, 'month').format(DateFormats.DAY),
  endDate: moment().format(DateFormats.DAY),
  buyerAccountId: '',
  locationId: '',
  supplierAccountId: '',
  bookingNumber: '',
  overageInvoiceNumber: '',
  durationType: '',
  sortBy: "CALENDAR_DATE",
  sortDir: 'desc',
  page: 0,
  size: 10,
};

const AdminSpaceUsageReport = () => {
  const { snackbarShowMessage } = useSnackbar();

  // state hooks
  const [reportQuery, setReportQuery] = useBindSearchQuery(DEFAULT_QUERY);
  const requestQuery = useMemo(() => withDateRangeBypass(reportQuery, ADMIN_SPACE_USAGE_DATE_BYPASS_KEYS), [reportQuery]);

  // Api hooks
  const {
    reportPayload,
    pageInfo,
    isLoading,
    error
  } =  useGetBoundReportPaginated<BookedSpaceCalendarView[]>({
    requestUrl: reportEndpoints.search,
    requestParams: requestQuery
  });

  const {
    reportSummary,
    isReportSummaryLoading,
    reportSummaryError
  } = useGetBoundReportSummary<SpaceUsageSummary>({
    requestUrl: reportEndpoints.summary,
    requestParams: requestQuery
  });

  const downloadUrl = useBoundQueryDownloadUrl({ endpoint: reportEndpoints.report, query: requestQuery });

  // error hooks
  useSnackbarDisplayOnError({error, snackbarShowMessage });
  useSnackbarDisplayOnError({error: reportSummaryError, snackbarShowMessage });

  // conditional display hooks
  // Not gated on `isLoading`: the previous payload survives a refetch, so
  // leaving this on keeps the empty state put instead of blinking out and
  // back on every filter change. It is undefined until the first load, so it
  // cannot flash before there is an answer.
  const shouldShowPersonAndBox = useMemo(() => {
    return !error && reportPayload?.length === 0
  }, [error, reportPayload]);

  const showPagination = useMemo(() => {
    return !!(reportPayload?.length && reportPayload.length > 0);
  }, [reportPayload])

  // change handlers
  const handleFilterChange = (newQuery: AdminSpaceUsageSearchParams) => {
    const updatedQuery = {
      ...reportQuery,
      ...newQuery
    };
    if (!newQuery.page) {
      // reset to first page on filter change if it is not being explicitly set
      updatedQuery.page = 0;
    }

    setReportQuery(updatedQuery);
  }

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <SecurSpaceReportTitle>Space Usage</SecurSpaceReportTitle>
        <AdminSpaceUsageSortFilter
          query={reportQuery}
          onChange={handleFilterChange}
        />
        <ReportSummaryWithLoader
          visible={!shouldShowPersonAndBox}
          isLoading={isReportSummaryLoading}
          mapping={adminSpaceUsageSummaryMapping}
          summary={reportSummary}
        />
        <ReportDataSummary
          visible={!shouldShowPersonAndBox}
          count={pageInfo?.count || 0}
          title={"All"}
          downloadButtonsSlot={
            <DownloadButtonsContainer>
              <DownloadCSVButton title='Download Report' reportUrl={downloadUrl} />
            </DownloadButtonsContainer>
          }
        />
        <AdminSpaceUsageCardListWithLoader
          visible={!error}
          isLoading={isLoading}
          items={reportPayload}
        />
        <PersonAndBoxMessage
          visible={shouldShowPersonAndBox }
        />
        <BoundTablePagination<AdminSpaceUsageSearchParams>
          visible={showPagination}
          pageInfo={pageInfo}
          onChange={handleFilterChange}
        />
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  );
};

export default AdminSpaceUsageReport;
